import React, { useMemo } from 'react';

const EmployeesTab = ({
  employees,
  roles,
  loading,
  employeeSearch,
  setEmployeeSearch,
  employeeRoleFilter,
  setEmployeeRoleFilter,
  employeeStatusFilter,
  setEmployeeStatusFilter,
  employeeSortBy,
  setEmployeeSortBy,
  employeeSortOrder,
  setEmployeeSortOrder,
  openCreateEmployeeModal,
  openEditEmployeeModal,
  handleDeactivateEmployee,
  handleResetPassword,
  viewEmployeeAvailability,
  clearEmployeeFilters
}) => {
  const filteredEmployees = useMemo(() => {
    let filtered = [...employees];

    if (employeeSearch) {
      const search = employeeSearch.toLowerCase();
      filtered = filtered.filter(emp =>
        emp.name?.toLowerCase().includes(search) ||
        emp.email?.toLowerCase().includes(search) ||
        emp.phone?.includes(search)
      );
    }

    if (employeeRoleFilter) {
      filtered = filtered.filter(emp =>
        emp.role_ids?.includes(employeeRoleFilter)
      );
    }

    if (employeeStatusFilter) {
      filtered = filtered.filter(emp => emp.status === employeeStatusFilter);
    }

    filtered.sort((a, b) => {
      let aVal, bVal;
      switch (employeeSortBy) {
        case 'name':
          aVal = a.name?.toLowerCase() || '';
          bVal = b.name?.toLowerCase() || '';
          break;
        case 'email':
          aVal = a.email?.toLowerCase() || '';
          bVal = b.email?.toLowerCase() || '';
          break;
        case 'status':
          aVal = a.status || '';
          bVal = b.status || '';
          break;
        default:
          aVal = a.name?.toLowerCase() || '';
          bVal = b.name?.toLowerCase() || '';
      }

      if (employeeSortOrder === 'asc') {
        return aVal < bVal ? -1 : aVal > bVal ? 1 : 0;
      }
      return aVal > bVal ? -1 : aVal < bVal ? 1 : 0;
    });

    return filtered;
  }, [employees, employeeSearch, employeeRoleFilter, employeeStatusFilter, employeeSortBy, employeeSortOrder]);

  const handleSort = (field) => {
    if (employeeSortBy === field) {
      setEmployeeSortOrder(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setEmployeeSortBy(field);
      setEmployeeSortOrder('asc');
    }
  };

  const getRoleColor = (roleId) => {
    const role = roles.find(r => r.role_id === roleId);
    return role?.color || '#666';
  };

  const getRoleName = (roleId) => {
    const role = roles.find(r => r.role_id === roleId);
    return role ? role.name : 'Unknown';
  };

  const getSortIndicator = (field) => {
    if (employeeSortBy !== field) return null;
    return employeeSortOrder === 'asc' ? ' ▲' : ' ▼';
  };

  return (
    <div className="employees-view">
      <div className="employees-toolbar">
        <div className="employees-filters">
          <div className="filter-group">
            <input
              type="text"
              placeholder="Search by name, email, or phone..."
              value={employeeSearch}
              onChange={(e) => setEmployeeSearch(e.target.value)}
              className="filter-search"
            />
          </div>
          <div className="filter-group">
            <select
              value={employeeRoleFilter}
              onChange={(e) => setEmployeeRoleFilter(e.target.value)}
              className="filter-select"
            >
              <option value="">All Roles</option>
              {roles.map(role => (
                <option key={role.role_id} value={role.role_id}>{role.name}</option>
              ))}
            </select>
          </div>
          <div className="filter-group">
            <select
              value={employeeStatusFilter}
              onChange={(e) => setEmployeeStatusFilter(e.target.value)}
              className="filter-select"
            >
              <option value="">All Status</option>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
            </select>
          </div>
          {(employeeSearch || employeeRoleFilter || employeeStatusFilter) && (
            <button className="btn btn-sm btn-secondary" onClick={clearEmployeeFilters}>
              Clear Filters
            </button>
          )}
        </div>
        <div className="employees-actions">
          <span className="results-count">{filteredEmployees.length} of {employees.length} employees</span>
          <button className="btn btn-primary" onClick={openCreateEmployeeModal}>
            + Add Employee
          </button>
        </div>
      </div>

      <div className="employees-table-container">
        <table className="employees-table">
          <thead>
            <tr>
              <th className="sortable" onClick={() => handleSort('name')}>
                Name{getSortIndicator('name') && <span className="sort-indicator">{getSortIndicator('name')}</span>}
              </th>
              <th className="sortable" onClick={() => handleSort('email')}>
                Email{getSortIndicator('email') && <span className="sort-indicator">{getSortIndicator('email')}</span>}
              </th>
              <th>Phone</th>
              <th>Roles</th>
              <th className="sortable" onClick={() => handleSort('status')}>
                Status{getSortIndicator('status') && <span className="sort-indicator">{getSortIndicator('status')}</span>}
              </th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredEmployees.length === 0 ? (
              <tr>
                <td colSpan="6" className="empty-table-message">
                  {employees.length === 0 ? 'No employees yet. Add your first employee.' : 'No employees match your filters.'}
                </td>
              </tr>
            ) : (
              filteredEmployees.map(employee => (
                <tr key={employee.employee_id} className={employee.status !== 'active' ? 'inactive-row' : ''}>
                  <td className="employee-name-cell">
                    <strong>{employee.name}</strong>
                  </td>
                  <td>{employee.email}</td>
                  <td>{employee.phone || '-'}</td>
                  <td>
                    <div className="role-tags">
                      {employee.role_ids?.length > 0 ? (
                        employee.role_ids.map(roleId => (
                          <span
                            key={roleId}
                            className="role-tag"
                            style={{ backgroundColor: getRoleColor(roleId) }}
                          >
                            {getRoleName(roleId)}
                          </span>
                        ))
                      ) : (
                        <span className="no-roles">No roles assigned</span>
                      )}
                    </div>
                  </td>
                  <td>
                    <span className={`status-badge ${employee.status}`}>
                      {employee.status}
                    </span>
                  </td>
                  <td>
                    <div className="table-actions">
                      <button className="btn btn-sm btn-info" onClick={() => viewEmployeeAvailability(employee)}>
                        Availability
                      </button>
                      <button className="btn btn-sm" onClick={() => openEditEmployeeModal(employee)}>
                        Edit
                      </button>
                      {employee.status === 'active' && (
                        <>
                          <button
                            className="btn btn-sm btn-secondary"
                            onClick={() => handleResetPassword(employee)}
                            disabled={loading}
                          >
                            Reset Password
                          </button>
                          <button
                            className="btn btn-sm btn-danger"
                            onClick={() => handleDeactivateEmployee(employee)}
                            disabled={loading}
                          >
                            Deactivate
                          </button>
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default EmployeesTab;
